"use client";
import { API_ENDPOINTS } from "../../../constants/config";
import { Camera } from "lucide-react";
import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-hot-toast";
import { extractErrorMessage, extractApiErrorMessage } from "../../../utils/error-handler";

const CreateStudentModal = ({
  isOpen,
  onClose,
  currentAcademicYear,
  classes,
  sections,
  onSuccess,
}) => {
  const [classData, setClassData] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    profileImage: null,
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    role: "student",
    admissionNumber: "",
    aadhaarCard: "",
    birthday: "",
    class: "",
    section: "",
    academicYear: currentAcademicYear,
    rollNumber: "",
    fatherName: "",
    fatherContactNumber: "",
    motherName: "",
    motherContactNumber: "",
    guardianName: "",
    guardianContactNumber: "",
    status: "active",
    bloodGroup: "",
    gender: "",
    phoneNumber: "",
    transportationType: "", // Add this new field
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
  });

  // Helper to determine the academic year start year used for admission numbers
  const getAdmissionStartYear = () => {
    if (currentAcademicYear && currentAcademicYear.startYear) {
      return currentAcademicYear.startYear.toString();
    }
    if (formData.academicYear && formData.academicYear.startYear) {
      return formData.academicYear.startYear.toString();
    }
    return null;
  };

  // Real-time validation function
  const validateField = (name, value) => {
    const errors = { ...fieldErrors };
    
    switch (name) {
      case "firstName":
        if (!value || value.trim() === "") {
          errors.firstName = "First name is required";
        } else if (value.length < 2) {
          errors.firstName = "First name must be at least 2 characters long";
        } else {
          delete errors.firstName;
        }
        break;
        
      case "lastName":
        // Last name is optional, but if provided, validate length
        if (value && value.trim() !== "" && value.length < 2) {
          errors.lastName = "Last name must be at least 2 characters long";
        } else {
          delete errors.lastName;
        }
        break;
        
      case "email":
        // Email is optional, but if provided, validate format
        if (value && value.trim() !== "") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors.email = "Please enter a valid email address";
          } else {
            delete errors.email;
          }
        } else {
          delete errors.email;
        }
        break;
        
      case "phoneNumber":
        // Phone number is optional, but if provided, validate format
        if (value && value.trim() !== "") {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ""))) {
            errors.phoneNumber = "Phone number must be exactly 10 digits";
          } else {
            delete errors.phoneNumber;
          }
        } else {
          delete errors.phoneNumber;
        }
        break;
        
      case "fatherContactNumber":
        if (!value || value.trim() === "") {
          errors.fatherContactNumber = "Father's contact number is required";
        } else {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ""))) {
            errors.fatherContactNumber = "Father's contact number must be exactly 10 digits";
          } else {
            delete errors.fatherContactNumber;
          }
        }
        break;
        
      case "motherContactNumber":
        if (!value || value.trim() === "") {
          errors.motherContactNumber = "Mother's contact number is required";
        } else {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ""))) {
            errors.motherContactNumber = "Mother's contact number must be exactly 10 digits";
          } else {
            delete errors.motherContactNumber;
          }
        }
        break;
        
      case "address.pincode":
        if (!value || value.trim() === "") {
          delete errors.pincode;
        } else {
          const pincodeRegex = /^[0-9]{6}$/;
          if (!pincodeRegex.test(value)) {
            errors.pincode = "Pincode must be exactly 6 digits";
          } else {
            delete errors.pincode;
          }
        }
        break;
        
      case "birthday":
        if (!value) {
          errors.birthday = "Birthday is required";
        } else {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 3 || age > 25) {
            errors.birthday = "Student age must be between 3 and 25 years";
          } else {
            delete errors.birthday;
          }
        }
        break;
        
      case "admissionNumber":
        if (!value || value.trim() === "") {
          errors.admissionNumber = "Admission number is required";
        } else {
          delete errors.admissionNumber;
        }
        break;
        
      case "rollNumber":
        // Roll number is optional
          delete errors.rollNumber;
        break;
        
      case "fatherName":
        if (!value || value.trim() === "") {
          errors.fatherName = "Father's name is required";
        } else {
          delete errors.fatherName;
        }
        break;
        
      case "motherName":
        if (!value || value.trim() === "") {
          errors.motherName = "Mother's name is required";
        } else {
          delete errors.motherName;
        }
        break;
        
      case "bloodGroup":
        if (!value) {
          errors.bloodGroup = "Blood group is required";
        } else {
          delete errors.bloodGroup;
        }
        break;
        
      default:
        break;
    }
    
    setFieldErrors(errors);
  };

  const generateAdmissionNumber = async () => {
    try {
      const token = localStorage.getItem("token");
      // Get all students to determine the next sequential number
      const studentsResponse = await axiosInstance.get(
        API_ENDPOINTS.usersByRole("student"),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const students = studentsResponse.data.data.users;
      let maxSequentialNumber = 0;

      // Determine the start year from the currently selected academic year
      const startYear = getAdmissionStartYear();
      if (!startYear) {
        throw new Error("Academic year startYear is missing while generating admission number");
      }

      // Find the highest sequential number for the current year
      students.forEach((student) => {
        if (student.admissionNumber) {
          const yearPart = student.admissionNumber.substring(0, 4);
          if (yearPart === startYear) {
            const sequentialPart = parseInt(
              student.admissionNumber.substring(5),
              10
            );
            if (!isNaN(sequentialPart)) {
              maxSequentialNumber = Math.max(
                maxSequentialNumber,
                sequentialPart
              );
            }
          }
        }
      });

      // Generate next sequential number
      const nextNumber = (maxSequentialNumber + 1).toString().padStart(3, "0");

      // Format: YYYYA001 (using start year)
      return `${startYear}A${nextNumber}`;
    } catch (error) {
      console.error("Error generating admission number:", error);
      // Fallback using the same academic year logic; if still missing,
      // fall back to the current calendar year to avoid empty values
      const startYear =
        getAdmissionStartYear() || new Date().getFullYear().toString();
      const randomNum = Math.floor(Math.random() * 999)
        .toString()
        .padStart(3, "0");
      return `${startYear}A${randomNum}`;
    }
  };

  useEffect(() => {
    const fetchSectionsByClass = async () => {
      if (!formData.class) {
        setAvailableSections([]);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(
          API_ENDPOINTS.sectionsByClass(formData.class),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setAvailableSections(response.data.data.sections);
      } catch (error) {
        toast.error(
          `Failed to fetch sections: ${
            error.response?.data?.message || error.message
          }`
        );
        setAvailableSections([]);
      }
    };

    fetchSectionsByClass();
  }, [formData.class]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.classes, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClassData(response.data.data.classes);
      } catch (error) {
        toast.error(
          `Failed to fetch classes: ${
            error.response?.data?.message || error.message
          }`
        );
      }
    };

    fetchClasses();
  }, []);

  // Clear field errors when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFieldErrors({});
    }
  }, [isOpen]);

  // Generate admission number as soon as modal opens
  useEffect(() => {
    const setAdmissionNumberOnOpen = async () => {
      if (!isOpen) return;
      if (formData.admissionNumber && formData.admissionNumber.trim() !== "") return;

      const generatedNumber = await generateAdmissionNumber();
      if (generatedNumber) {
        setFormData((prev) => ({
          ...prev,
          admissionNumber: generatedNumber,
        }));
      }
    };

    setAdmissionNumberOnOpen();
  }, [isOpen, currentAcademicYear]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value,
        },
      }));
      // Validate address fields
      if (addressField === "pincode") {
        validateField("address.pincode", value);
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
    
    // Call real-time validation for the changed field
    validateField(name, value);
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      profileImage: file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");

      // Enhanced frontend validation with specific error messages
      const validationErrors = [];

      // Basic required field validation
      if (!formData.firstName || formData.firstName.trim() === "") {
        validationErrors.push("First name is required");
      } else if (formData.firstName.length < 2) {
        validationErrors.push("First name must be at least 2 characters long");
      }

      // Last name is optional, but if provided, validate length
      if (formData.lastName && formData.lastName.trim() !== "" && formData.lastName.length < 2) {
        validationErrors.push("Last name must be at least 2 characters long");
      }

      // Email is optional, but if provided, validate format
      if (formData.email && formData.email.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          validationErrors.push("Please enter a valid email address");
        }
      }

      // Phone number is optional, but if provided, validate format
      if (formData.phoneNumber && formData.phoneNumber.trim() !== "") {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.phoneNumber.replace(/\s/g, ""))) {
          validationErrors.push("Phone number must be exactly 10 digits");
        }
      }

      if (!formData.admissionNumber || formData.admissionNumber.trim() === "") {
        // If admission number is missing, attempt to auto-generate one once more
        const autoNumber = await generateAdmissionNumber();
        if (autoNumber) {
          formData.admissionNumber = autoNumber;
        } else {
          validationErrors.push("Admission number is required");
        }
      }

      // Roll number is optional - no validation needed

      if (!formData.fatherName || formData.fatherName.trim() === "") {
        validationErrors.push("Father's name is required");
      }

      if (!formData.fatherContactNumber || formData.fatherContactNumber.trim() === "") {
        validationErrors.push("Father's contact number is required");
      } else {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.fatherContactNumber.replace(/\s/g, ""))) {
          validationErrors.push("Father's contact number must be exactly 10 digits");
        }
      }

      if (!formData.motherName || formData.motherName.trim() === "") {
        validationErrors.push("Mother's name is required");
      }

      if (!formData.motherContactNumber || formData.motherContactNumber.trim() === "") {
        validationErrors.push("Mother's contact number is required");
      } else {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.motherContactNumber.replace(/\s/g, ""))) {
          validationErrors.push("Mother's contact number must be exactly 10 digits");
        }
      }

      if (!formData.birthday) {
        validationErrors.push("Birthday is required");
      } else {
        const birthDate = new Date(formData.birthday);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 3 || age > 25) {
          validationErrors.push("Student age must be between 3 and 25 years");
        }
      }

      if (!formData.bloodGroup) {
        validationErrors.push("Blood group is required");
      }

      // Address is optional. If user starts filling address, validate all address fields.
      const hasAnyAddressField = formData.address && (
        (formData.address.street && formData.address.street.trim() !== "") ||
        (formData.address.city && formData.address.city.trim() !== "") ||
        (formData.address.state && formData.address.state.trim() !== "") ||
        (formData.address.pincode && formData.address.pincode.trim() !== "")
      );
      if (hasAnyAddressField) {
        if (!formData.address.street || formData.address.street.trim() === "") {
          validationErrors.push("Street address is required");
        }

        if (!formData.address.city || formData.address.city.trim() === "") {
          validationErrors.push("City is required");
        }

        if (!formData.address.state || formData.address.state.trim() === "") {
          validationErrors.push("State is required");
        }

        if (!formData.address.pincode || formData.address.pincode.trim() === "") {
          validationErrors.push("Pincode is required");
        } else {
          const pincodeRegex = /^[0-9]{6}$/;
          if (!pincodeRegex.test(formData.address.pincode)) {
            validationErrors.push("Pincode must be exactly 6 digits");
          }
        }
      }

      // Show frontend validation errors
      if (validationErrors.length > 0) {
        toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`);
        setIsSubmitting(false);
        return;
      }

      // Create a FormData object to send the file
      const formDataToSend = new FormData();

      // Append the profile image if it exists
      if (formData.profileImage) {
        formDataToSend.append("file", formData.profileImage);
      }

      // Create a copy of the form data without the profileImage
      const dataToSend = {
        ...formData,
        address: {
          ...formData.address,
          country: "India",
        },
      };
      delete dataToSend.profileImage;

      // Append the JSON data
      formDataToSend.append("data", JSON.stringify(dataToSend));

      // Send the FormData with the correct Content-Type header
      const response = await axiosInstance.post(
        API_ENDPOINTS.usersCreate,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(
        "Student created successfully. Login credentials have been sent to the provided email address."
      );

      onClose();
      onSuccess?.();

      // Reset form data
      setFormData({
        profileImage: null,
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        role: "student",
        admissionNumber: "",
        aadhaarCard: "",
        birthday: "",
        class: "",
        section: "",
        academicYear: currentAcademicYear,
        rollNumber: "",
        fatherName: "",
        fatherContactNumber: "",
        motherName: "",
        motherContactNumber: "",
        guardianName: "",
        guardianContactNumber: "",
        status: "active",
        bloodGroup: "",
        gender: "",
        phoneNumber: "",
        transportationType: "",
        address: {
          street: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
        },
      });
      
      // Clear field errors
      setFieldErrors({});
    } catch (error) {
      console.error("Failed to create student:", error);
      
      // Use the enhanced error handling utility
      const errorMessage = extractApiErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex h-[100dvh] w-[100vw] max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100vw-2rem)] sm:max-w-4xl sm:rounded-lg sm:border">
        <DialogHeader className="shrink-0 border-b px-4 py-4 sm:px-6">
          <DialogTitle>Add New Student</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <div className="mb-4 rounded-md bg-blue-50 p-3 text-blue-700">
            <p className="text-sm">
              <strong>Note:</strong> Login credentials will be automatically
              generated and sent to the provided email address.
            </p>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="col-span-1 mb-2 flex justify-center md:col-span-2 xl:col-span-3">
                  <div className="relative w-28 sm:w-32">
                    <div className="group aspect-square w-full overflow-hidden rounded-full border-2 border-gray-200">
                    {formData.profileImage ? (
                      <img
                        src={
                          URL.createObjectURL(formData.profileImage) ||
                          "/placeholder.svg"
                        }
                        alt="Profile Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <svg
                          className="w-12 h-12 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}
                    <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                      <Camera className="w-8 h-8 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                />
                {fieldErrors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Middle Name</label>
                <Input
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Last Name</label>
                <Input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
                {fieldErrors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.lastName}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Email</label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
                {fieldErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Phone Number</label>
                <Input
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
                {fieldErrors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.phoneNumber}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Admission Number *
                </label>
                <Input
                  name="admissionNumber"
                  required
                  value={formData.admissionNumber}
                  onChange={handleChange}
                />
                {fieldErrors.admissionNumber && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.admissionNumber}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Roll Number</label>
                <Input
                  name="rollNumber"
                  value={formData.rollNumber}
                  onChange={handleChange}
                />
                {fieldErrors.rollNumber && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.rollNumber}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Aadhaar Card</label>
                <Input
                  name="aadhaarCard"
                  value={formData.aadhaarCard}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Father's Name *</label>
                <Input
                  name="fatherName"
                  required
                  value={formData.fatherName}
                  onChange={handleChange}
                />
                {fieldErrors.fatherName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.fatherName}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Father's Contact Number *
                </label>
                <Input
                  name="fatherContactNumber"
                  required
                  value={formData.fatherContactNumber}
                  onChange={handleChange}
                />
                {fieldErrors.fatherContactNumber && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.fatherContactNumber}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Mother's Name *</label>
                <Input
                  name="motherName"
                  required
                  value={formData.motherName}
                  onChange={handleChange}
                />
                {fieldErrors.motherName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.motherName}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Mother's Contact Number *
                </label>
                <Input
                  name="motherContactNumber"
                  required
                  value={formData.motherContactNumber}
                  onChange={handleChange}
                />
                {fieldErrors.motherContactNumber && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.motherContactNumber}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Guardian's Name</label>
                <Input
                  name="guardianName"
                  value={formData.guardianName}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Guardian's Contact Number
                </label>
                <Input
                  name="guardianContactNumber"
                  value={formData.guardianContactNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Birthday *</label>
                <Input
                  name="birthday"
                  type="date"
                  required
                  value={formData.birthday}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
                {fieldErrors.birthday && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.birthday}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Class</label>
                <Select
                  name="class"
                  value={formData.class}
                  onValueChange={(value) =>
                    handleChange({ target: { name: "class", value } })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classData.map((cls) => (
                      <SelectItem key={cls._id} value={cls._id}>
                        Class {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Section</label>
                <Select
                  value={formData.section}
                  onValueChange={(value) =>
                    handleChange({ target: { name: "section", value } })
                  }
                  disabled={!formData.class}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        formData.class ? "Select section" : "Select class first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSections.map((section) => (
                      <SelectItem key={section._id} value={section._id}>
                        Section {section.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Blood Group *</label>
                <Select
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onValueChange={(value) =>
                    handleChange({ target: { name: "bloodGroup", value } })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Blood Group" />
                  </SelectTrigger>
                  <SelectContent>
                    {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                      (group) => (
                        <SelectItem key={group} value={group}>
                          {group}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {fieldErrors.bloodGroup && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.bloodGroup}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Gender</label>
                <Select
                  name="gender"
                  value={formData.gender}
                  onValueChange={(value) =>
                    handleChange({ target: { name: "gender", value } })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Male", "Female", "Other"].map((gender) => (
                      <SelectItem key={gender} value={gender}>
                        {gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">
                  Transportation Type
                </label>
                <Select
                  name="transportationType"
                  value={formData.transportationType}
                  onValueChange={(value) =>
                    handleChange({
                      target: { name: "transportationType", value },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Transportation Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bus">Bus</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="self">Self</SelectItem>
                  </SelectContent>
                </Select>
              </div>
                <div className="col-span-1 space-y-4 md:col-span-2 xl:col-span-3">
                <h3 className="text-lg font-medium">Address Details</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">
                      Street Address
                    </label>
                    <Input
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleChange}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">City</label>
                    <Input
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleChange}
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">State</label>
                    <Select
                      name="address.state"
                      value={formData.address.state}
                      onValueChange={(value) =>
                        handleChange({ target: { name: "address.state", value } })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "Andhra Pradesh",
                          "Arunachal Pradesh",
                          "Assam",
                          "Bihar",
                          "Chhattisgarh",
                          "Goa",
                          "Gujarat",
                          "Haryana",
                          "Himachal Pradesh",
                          "Jharkhand",
                          "Karnataka",
                          "Kerala",
                          "Madhya Pradesh",
                          "Maharashtra",
                          "Manipur",
                          "Meghalaya",
                          "Mizoram",
                          "Nagaland",
                          "Odisha",
                          "Punjab",
                          "Rajasthan",
                          "Sikkim",
                          "Tamil Nadu",
                          "Telangana",
                          "Tripura",
                          "Uttar Pradesh",
                          "Uttarakhand",
                          "West Bengal",
                        ].map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Pincode</label>
                    <Input
                      name="address.pincode"
                      value={formData.address.pincode}
                      onChange={handleChange}
                      placeholder="Pincode"
                      maxLength={6}
                      pattern="[0-9]*"
                    />
                    {fieldErrors.pincode && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.pincode}</p>
                    )}
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t px-4 py-4 sm:px-6">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting ? "Creating..." : "Create Student"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateStudentModal;
